import { type } from 'arktype'

// Define the feedback request types enum (reordered to match backend)
const REQUEST_TYPES = ['bug', 'feature', 'improvement', 'general'] as const

// ArkType schema for feedback modal validation
export const feedbackSchema = type({
    request_type: "'bug'|'feature'|'improvement'|'general'",
    description: 'string', // Changed from feedback_text to match backend
    wants_follow_up: 'boolean',
}).onUndeclaredKey('reject')

// Custom validation function with user-friendly error messages
export const validateFeedbackForm = (values: {
    request_type: string
    description: string // Changed from feedback_text to match backend
    wants_follow_up: boolean
}) => {
    const errors: Record<string, { type: string; message: string }> = {}

    // Request type validation
    if (!values.request_type) {
        errors.request_type = {
            type: 'required',
            message: 'Please select a feedback type',
        }
    } else if (!REQUEST_TYPES.includes(values.request_type as FeedbackRequestType)) {
        errors.request_type = {
            type: 'invalid',
            message: 'Please select a valid feedback type',
        }
    }

    // Description validation with character count
    const trimmedText = values.description ? values.description.trim() : ''

    if (!trimmedText) {
        errors.description = {
            type: 'required',
            message: 'Please provide your feedback',
        }
    } else if (trimmedText.length < 10) {
        errors.description = {
            type: 'minLength',
            message: `Feedback must be at least 10 characters (currently ${trimmedText.length})`,
        }
    } else if (trimmedText.length > 2000) {
        errors.description = {
            type: 'maxLength',
            message: `Feedback must be no more than 2000 characters (currently ${trimmedText.length})`,
        }
    }

    // wants_follow_up validation (should always be boolean, defaulting to false)
    if (typeof values.wants_follow_up !== 'boolean') {
        errors.wants_follow_up = {
            type: 'invalid',
            message: 'Invalid follow-up preference',
        }
    }

    return {
        values:
            Object.keys(errors).length === 0
                ? {
                      ...values,
                      description: trimmedText, // Return trimmed text
                  }
                : {},
        errors,
    }
}

// Custom resolver for react-hook-form integration
export const feedbackFormResolver = (values: {
    request_type: string
    description: string // Changed from feedback_text to match backend
    wants_follow_up: boolean
}) => {
    return validateFeedbackForm(values)
}

// TypeScript types
export type FeedbackRequestType = (typeof REQUEST_TYPES)[number]

// Constants for form validation
export const VALIDATION_CONSTANTS = {
    MIN_CHARACTERS: 10,
    MAX_CHARACTERS: 2000,
    REQUEST_TYPES,
} as const
